"use client";

import {
  useController,
  useFormContext,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { BackendInput } from "../../backend-input";
import { BackendLabel } from "../../backend-label";
import { cn } from "@/lib/utils";

export type ControlledPhoneInputProps<
  TFieldValues extends FieldValues = FieldValues,
> = {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  labelClassName?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  readOnly?: boolean;
};

export function ControlledPhoneInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  label,
  placeholder = "+491234567890",
  required,
  labelClassName,
  className,
  inputClassName,
  disabled,
  readOnly,
}: ControlledPhoneInputProps<TFieldValues>) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control ?? formContext?.control;

  if (!actualControl) {
    throw new Error(
      "Controlled phone input must be used inside FormProvider or receive a control prop"
    );
  }

  const {
    field: {
      name: fieldName,
      value: fieldValue,
      onChange: fieldOnChange,
      onBlur: fieldOnBlur,
      ref: fieldRef,
    },
    fieldState: { error },
  } = useController({ name, control: actualControl });

  // Stable id based on field name to avoid SSR/CSR id drift
  const inputId = `${name}-field`.replace(/\./g, "-");

  return (
    <div className={cn("flex flex-col gap-1 sm:gap-1.5", className)}>
      {label && (
        <BackendLabel
          htmlFor={inputId}
          required={required}
          className={labelClassName}
        >
          {label}
        </BackendLabel>
      )}

      <BackendInput
        id={inputId}
        name={fieldName}
        value={fieldValue ?? ""}
        onChange={(e) => fieldOnChange(e.target.value)}
        onBlur={fieldOnBlur}
        ref={fieldRef}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        className={inputClassName}
        error={error?.message}
        aria-invalid={error ? "true" : "false"}
      />
    </div>
  );
}
